import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import InfoIcon from "@mui/icons-material/Info";
import { t } from "i18next";
import {
  ConcreteStrengthType,
  IPdfDetails,
  IProjectPourCardData,
  UserRoleEnum,
} from "@/models";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, Edit, MenuIcon, PdfExport, ProgressCircle } from "@/components";
// OVERDESIGN FEATURE - CIRCLES FOR THE SUMMIT CO2
// import Co2Circle from '@/components/co2-circle';
// import ExcessCircle from '@/components/excess-circle';
import FlexWrapper from "../flex-wrapper";
import { loadingStore, useAppStore, useProjectStore } from "@/stores";
import { LoadingKeys, routesDetails } from "@/constants";
import { useNavigate } from "react-router";
import { styled } from "@mui/material/styles";
import LoadingOverlayComp from "../loading-overlay";
import { useDateTimeFormat } from "@/hooks";
import { mpaToPsi, cubeToCelinder } from "@/utils";

const getPourStrengthPercentage = (
  concreteType: ConcreteStrengthType,
  highestKnownStrength: number = 0,
  pourStartTime: any
) => {
  let percentage = 0;

  try {
    const concreteStrength = Number(concreteType.replace("B", ""));

    if (!isNaN(concreteStrength) && concreteStrength !== 0) {
      percentage = (highestKnownStrength / concreteStrength) * 100;
    }
  } catch (error) {
    toast.error("Concrete type has a wrong value");
  }
  return percentage === 0 && pourStartTime !== null ? 0.01 : percentage;
};

const getOverDesign = (
  concreteType: ConcreteStrengthType,
  highestKnownStrength: number = 0
) => {
  const baseStrength = Number(concreteType.replace("B", ""));
  return Math.max(0, highestKnownStrength - baseStrength);
};

export const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.grey.A100,
  filter: "drop-shadow(0px 4px 8px rgba(0, 153, 153, 0.1))",
  margin: "12px 0",
  boxShadow: "none",
  borderRadius: "12px",
  minHeight: "auto",
  transform: "translateZ(0)", // Forces GPU rendering layer
  backfaceVisibility: "hidden", // Prevents flickering
  willChange: "transform", // Hint to browser: this might change
  "& .MuiCardContent-root": {
    padding: "9px 16px",
    color: theme.palette.common.black,
  },
}));
export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.black.darker,
  minHeight: "auto",
  display: "flex",
  alignItems: "center",
  gap: "12px",
}));

interface PourCardProps {
  pourDetails: IProjectPourCardData;
  handleOpenPopConfirmModal: (pourId: number) => void;
  handlePdfExport: (pdfDet: IPdfDetails, pourId: number) => void;
  projectId: string;
  isMetric: boolean;
  isCylinder: boolean;
  projectName: string;
  last_humidity: any;
  use_humidity: boolean;
  testid : number;
}

const PourCard = ({
  pourDetails,
  handleOpenPopConfirmModal,
  projectId,
  isMetric,
  isCylinder,
  projectName,
  last_humidity,
  use_humidity,
  handlePdfExport,
  testid
}: PourCardProps) => {
  const getStrength = useCallback(
    (strength: number) => {
      if (!isMetric) {
        if (isCylinder) {
          return mpaToPsi(cubeToCelinder(strength))?.toFixed();
        }
        return mpaToPsi(strength)?.toFixed();
      } else {
        if (isCylinder) {
          return parseInt(cubeToCelinder(strength)?.toFixed());
        }
        return parseInt(strength?.toFixed());
      }
    },
    [isMetric, isCylinder]
  );
  const navigate = useNavigate();
  const { formatDate } = useDateTimeFormat();
  const { currentExportPourId } = useProjectStore();
  const { currentUser } = useAppStore();
  const { getLoading } = loadingStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const open = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const {
    pour_id,
    pour_name,
    errors,
    pour_start_time,
    highest_known_strengh = 0,
    b_type_name,
    status,
    expected_finish,
    finish_at,
    active,
  } = pourDetails || {};

  return (
    <>
      <StyledCard data-testid={testid}>
        {currentExportPourId === pour_id &&
          getLoading(LoadingKeys.ExportPourDetails) && <LoadingOverlayComp />}

        <CardContent
          onClick={() =>
            navigate(
              routesDetails.Sensors(
                projectId,
                String(pour_id),
                status
              ),
              {
                state: { errors }, // Pass errors through state
              }
            )
          }
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <FlexWrapper alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" component="h5" color="common.black">
                {pour_name}
              </Typography>
              <Typography variant="body2" color="common.black">{`${t(
                "concrete"
              )}: ${b_type_name}`}</Typography>
              <Typography variant="body2" color="common.black">{`${t(
                "pourTime"
              )}: ${formatDate(pour_start_time, "time")}`}</Typography>
              <Typography variant="body2" color="common.black">
                {active ? (
                  <>
                    {`${t("expectedFinish")}: `}
                    {formatDate(expected_finish, "time") ?? "N/A"}
                  </>
                ) : (
                  <>
                    {`${t("finished")}: `}
                    {formatDate(finish_at, "time") ?? "N/A"}
                  </>
                )}
              </Typography>

              {errors && errors.length > 0 && (
                  <Tooltip
                    PopperProps={{
                      disablePortal: true,
                    }}
                    onClose={(event) => {
                      event.stopPropagation();
                      setTooltipOpen(false);
                    }}
                    open={tooltipOpen}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    arrow
                    title={errors.map(({event_type}) => (
                      <>
                        {event_type} <br />
                      </>
                    ))}
                    placement="top-start"
                  >
                    <IconButton
                      sx={{ color: "red" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setTooltipOpen(!tooltipOpen);
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
              )}
            </Box>

            <Box sx={{ alignSelf: "flex-start" }}>
              <IconButton
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleMenuClick}
                aria-label="options"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={(e) => handleClose(e)}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <StyledMenuItem
                  data-testid="mark-as-done-menu-item"
                  disabled={status === 'finished' || currentUser?.user?.role === UserRoleEnum.customerMngr}
                  onClick={(e) => {
                    handleOpenPopConfirmModal(pour_id);
                    handleClose(e);
                  }}
                >
                  <span>
                    <Check />
                  </span>
                  {t("markAsDone")}
                </StyledMenuItem>

                <StyledMenuItem
                  data-testid="pdf-export-menu-item"
                  onClick={(e) => {
                    handlePdfExport(
                      {
                        pourDataName: pour_name,
                        concreteType: b_type_name,
                        pourStartTime: pour_start_time,
                      },
                      pour_id
                    );
                    handleClose(e);
                  }}
                >
                  <span>
                    <PdfExport />
                  </span>
                  {t("pdfExport")}
                </StyledMenuItem>
                <StyledMenuItem
                  data-testid="edit-menu-item"
                  disabled={status === 'finished' || currentUser?.user?.role === UserRoleEnum.customerMngr}
                  onClick={(e) => {
                    navigate(routesDetails.editPour(projectId, pour_id, status === 'new'));
                    handleClose(e);
                  }}
                >
                  <span>
                    <Edit />
                  </span>
                  {t("editPour")}
                </StyledMenuItem>
              </Menu>
            </Box>
          </FlexWrapper>
          <Box sx={{ mb: 3 }}>
            <FlexWrapper
              alignItems="flex-end" // Align components to the bottom
              justifyContent="center" // Center them horizontally
              sx={{
                gap: "12px", // Adjust the gap between the components (you can modify this value)
              }}
            >
              <ProgressCircle
                data-testid="progress-circle"
                contentUnit={isMetric ? t("mpa") : t("psi")}
                contentNumber={getStrength(highest_known_strengh) as any}
                percentage={getPourStrengthPercentage(
                  pourDetails.b_type_name,
                  highest_known_strengh,
                  pour_start_time
                )}
                last_humidity={last_humidity}
                use_humidity={use_humidity}
              />
            </FlexWrapper>
          </Box>
        </CardContent>
      </StyledCard>
    </>
  );
};

export default PourCard;
